import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { readPref, writePref, currentPrefsUserId } from '../utils/userPrefs';
import { AuthContext } from './AuthContext';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Collapsed state — persisted per signed-in user.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = readPref('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Unlike every other consumer of userPrefs, this provider sits at the app
  // ROOT, so its initializer runs before anyone has signed in. On a fresh login
  // in the same tab (rather than a page reload) there is no user yet, the
  // initializer above falls back to the default, and nothing would re-read it.
  // Re-read when the signed-in user changes, so the collapse state from their
  // last session applies without a refresh — and so switching accounts picks up
  // the new one rather than keeping the previous account's.
  // Read the context directly rather than through useAuth, which THROWS outside
  // an AuthProvider. This provider is mounted at the app root and in tests that
  // render it on its own; a preference refresh must never be able to take the
  // whole shell down. Optional chaining degrades to "no user known", which is
  // exactly the pre-existing behaviour.
  const auth = useContext(AuthContext);
  const userId = auth?.currentUser?.id;
  const hydratedForUserRef = useRef(currentPrefsUserId());
  useEffect(() => {
    const uid = userId == null ? null : String(userId);
    if (uid === hydratedForUserRef.current) return;
    hydratedForUserRef.current = uid;
    if (!uid) return; // signing out leaves the current view alone
    const saved = readPref('sidebarCollapsed', uid);
    setSidebarCollapsed(saved ? JSON.parse(saved) : false);
  }, [userId]);

  // Save collapsed state to localStorage and update body class
  useEffect(() => {
    writePref('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    // Add/remove body class for global styling
    if (sidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  }, [sidebarCollapsed]);

  // Initialize body class on mount
  useEffect(() => {
    const saved = readPref('sidebarCollapsed');
    if (saved && JSON.parse(saved)) {
      document.body.classList.add('sidebar-collapsed');
    }
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const openSidebar = () => {
    setSidebarOpen(true);
  };

  // Toggle collapsed state
  const toggleCollapsed = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const collapseSidebar = useCallback(() => {
    setSidebarCollapsed(true);
  }, []);

  const expandSidebar = useCallback(() => {
    setSidebarCollapsed(false);
  }, []);

  // Sidebar scroll position — persists across page navigations so the sidebar
  // doesn't jump to the top when the component remounts on route changes.
  const sidebarScrollRef = useRef(0);

  const setSidebarScroll = useCallback((scrollTop) => {
    sidebarScrollRef.current = scrollTop;
  }, []);

  const getSidebarScroll = useCallback(() => {
    return sidebarScrollRef.current;
  }, []);

  /*
   * Shell mounting
   * -------------
   * The sidebar UI is rendered ONCE by <AppShell/>, outside <Routes>, so it
   * survives navigation instead of unmounting with each page. Pages don't
   * render it directly — they render the zero-DOM <DashboardSidebar/> slot,
   * which just calls requestSidebar() here.
   *
   * Why a request count rather than a route allowlist: 42 pages render the
   * sidebar, and a hardcoded path list would silently drop it the first time
   * someone adds a dashboard page. Keeping the declaration in the page means
   * it stays correct by construction.
   *
   * `sidebarEverRequested` latches true and never resets. During a page→page
   * navigation React unmounts the old page before mounting the new one, so
   * the count momentarily hits 0; unmounting on that would refetch and lose
   * scroll — exactly what this exists to avoid. Once mounted the sidebar
   * stays mounted and is merely hidden when no page wants it.
   */
  const [sidebarRequestCount, setSidebarRequestCount] = useState(0);
  const [sidebarEverRequested, setSidebarEverRequested] = useState(false);

  const requestSidebar = useCallback(() => {
    setSidebarRequestCount((n) => n + 1);
    setSidebarEverRequested(true);
    return () => setSidebarRequestCount((n) => Math.max(0, n - 1));
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + B to toggle collapse
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        toggleCollapsed();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleCollapsed]);

  return (
    <SidebarContext.Provider value={{
      sidebarOpen,
      toggleSidebar,
      closeSidebar,
      openSidebar,
      sidebarCollapsed,
      toggleCollapsed,
      collapseSidebar,
      expandSidebar,
      setSidebarScroll,
      getSidebarScroll,
      requestSidebar,
      sidebarVisible: sidebarRequestCount > 0,
      sidebarEverRequested
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
